import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { DateFilter } from '@/components/DateFilter';
import { FileText, Search, Building2, Download, Printer } from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';

export default function AccountStatementPage() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(''); // value = `${code}|${name}`
  const [data, setData] = useState(null);
  const [dateFilter, setDateFilter] = useState({});
  const [branchId, setBranchId] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('branch') || new URLSearchParams(window.location.search).get('branch_id') || localStorage.getItem('report_branch_ctx') || 'all'; } catch { return 'all'; }
  });  // iter945 — honour the hub's Global Branch Context
  const [loading, setLoading] = useState(false);

  const { branches, fetchBranches } = useBranchStore();
  const [tableSearch, setTableSearch] = useState('');

  useEffect(() => {
    fetchBranches();
    api.get('/accounting/accounts').then(r => setAccounts(r.data || [])).catch(() => {});
  }, [fetchBranches]);

  const fetchStatement = async () => {
    if (!selectedAccount) { toast.error('Select an account'); return; }
    const [code, name] = selectedAccount.split('|');
    setLoading(true);
    try {
      const params = {};
      if (code) params.account_code = code;
      if (name) params.account_name = name;
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
      if (branchId && branchId !== 'all') params.branch_id = branchId;
      const res = await api.get('/accounting/account-statement', { params });
      setData(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  // Client-side CSV export — shape matches the on-screen table 1:1.
  const exportCSV = () => {
    if (!data?.transactions?.length) { toast.error('Nothing to export'); return; }
    const header = ['#', 'Date', 'Entry #', 'Description', 'Reference', 'Debit', 'Credit', 'Balance'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [];
    let idx = 0;
    if (dateFilter.start) {
      idx += 1;
      rows.push([idx, dateFilter.start, '', 'Opening balance (brought forward)', '', '', '', data.opening_balance ?? 0].map(esc).join(','));
    }
    data.transactions.forEach((t) => {
      idx += 1;
      rows.push([
        idx,
        (t.date || '').slice(0, 10),
        t.entry_number || '',
        t.description || '',
        t.reference || '',
        t.debit || 0,
        t.credit || 0,
        t.balance || 0,
      ].map(esc).join(','));
    });
    const csv = [header.map(esc).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `account_statement_${(data.account_name || 'account').replace(/[^a-z0-9]+/gi, '_')}_${localToday()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Print → user can save as PDF via browser print dialog.
  const printStatement = () => window.print(); // eslint-print-native-ok — desktop admin page, browser dialog is fine

  return (
    <DashboardLayout>
      <div className="space-y-6 print:space-y-3" data-testid="account-statement-page">
        <div className="flex items-center justify-between flex-wrap gap-4 print:hidden">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-200/50">
              <FileText size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Account Statement</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Transaction history for a specific account</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <DateFilter onFilterChange={setDateFilter} />
            {data && data.transactions?.length > 0 && (
              <>
                <Button size="sm" variant="outline" className="h-8 text-xs rounded-full" onClick={exportCSV} data-testid="export-stmt-csv">
                  <Download size={12} className="mr-1" />CSV
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs rounded-full" onClick={printStatement} data-testid="export-stmt-print">
                  <Printer size={12} className="mr-1" />Print / PDF
                </Button>
              </>
            )}
          </div>
        </div>

        <Card className="print:hidden">
          <CardContent className="pt-5">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-6">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Account</label>
                <Select value={selectedAccount || 'none'} onValueChange={v => setSelectedAccount(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select account</SelectItem>
                    {accounts.map(a => (
                      <SelectItem key={a.id} value={`${a.code}|${a.name}`}>
                        {a.code} — {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-4">
                <label className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                  <Building2 size={12} /> Branch
                </label>
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger data-testid="account-branch-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {(branches || []).map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Button onClick={fetchStatement} disabled={loading} className="w-full" data-testid="fetch-statement-btn">
                  <Search size={14} className="mr-1" />{loading ? 'Loading…' : 'Get Statement'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {data && (
          <>
            <div className="hidden print:block mb-3">
              <h2 className="text-xl font-bold">{data.account_name}</h2>
              <p className="text-xs text-muted-foreground">
                Period: {data.period?.start} to {data.period?.end}
                {branchId !== 'all' && ` • Branch: ${(branches.find(b => b.id === branchId) || {}).name || branchId}`}
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 print:gap-2">
              <Card><CardContent className="pt-5 print:pt-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Transactions</p><p className="text-2xl font-bold print:text-lg" data-testid="stmt-count">{data.transaction_count}</p></CardContent></Card>
              <Card><CardContent className="pt-5 print:pt-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Opening Balance</p><p className="text-2xl font-bold text-muted-foreground print:text-lg" data-testid="stmt-opening">{cur()} {((data.opening_balance ?? 0)).toLocaleString()}</p></CardContent></Card>
              <Card><CardContent className="pt-5 print:pt-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total Debit</p><p className="text-2xl font-bold text-success print:text-lg">{cur()} {(data.total_debit || 0).toLocaleString()}</p></CardContent></Card>
              <Card><CardContent className="pt-5 print:pt-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total Credit</p><p className="text-2xl font-bold text-error print:text-lg">{cur()} {(data.total_credit || 0).toLocaleString()}</p></CardContent></Card>
              <Card><CardContent className="pt-5 print:pt-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Closing Balance</p><p className="text-2xl font-bold text-info print:text-lg">{cur()} {(data.closing_balance || 0).toLocaleString()}</p></CardContent></Card>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 print:hidden" data-testid="stmt-toolbar">
                  <div className="relative flex-1 min-w-[220px]">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      value={tableSearch}
                      onChange={e => setTableSearch(e.target.value)}
                      placeholder="Filter rows by entry #, description or reference…"
                      className="w-full h-8 pl-7 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                      data-testid="stmt-search"
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground">{
                    (() => {
                      const q = tableSearch.trim().toLowerCase();
                      if (!q) return `${data.transactions?.length || 0} rows`;
                      const c = (data.transactions || []).filter(t =>
                        (t.entry_number || '').toLowerCase().includes(q)
                        || (t.description || '').toLowerCase().includes(q)
                        || (t.reference || '').toLowerCase().includes(q)
                      ).length;
                      return `${c} of ${data.transactions?.length || 0}`;
                    })()
                  }</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b"><tr>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Entry #</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Description</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Reference</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Debit</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Credit</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Balance</th>
                    </tr></thead>
                    <tbody>
                      {/* iter431 — Opening balance carry-forward row when a date range is set */}
                      {dateFilter.start && (
                        <tr className="border-b bg-muted/30 font-medium" data-testid="stmt-opening-row">
                          <td className="px-4 py-3 text-muted-foreground">{dateFilter.start}</td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">—</td>
                          <td className="px-4 py-3 italic text-muted-foreground" colSpan={2}>Opening balance (brought forward)</td>
                          <td className="px-4 py-3 text-right text-muted-foreground">-</td>
                          <td className="px-4 py-3 text-right text-muted-foreground">-</td>
                          <td className="px-4 py-3 text-right font-bold text-muted-foreground">{(data.opening_balance ?? 0).toLocaleString()}</td>
                        </tr>
                      )}
                      {data.transactions?.filter(t => {
                        const q = tableSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (t.entry_number || '').toLowerCase().includes(q)
                          || (t.description || '').toLowerCase().includes(q)
                          || (t.reference || '').toLowerCase().includes(q);
                      }).map((t, i) => (
                        <tr key={i} className="border-b hover:bg-muted/50">
                          <td className="px-4 py-3 text-muted-foreground">{t.date?.slice(0, 10)}</td>
                          <td className="px-4 py-3 font-mono text-xs">{t.entry_number}</td>
                          <td className="px-4 py-3">{t.description}</td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">{t.reference}</td>
                          <td className="px-4 py-3 text-right font-medium text-success">{t.debit > 0 ? t.debit.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-right font-medium text-error">{t.credit > 0 ? t.credit.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-right font-bold">{t.balance?.toLocaleString()}</td>
                        </tr>
                      ))}
                      {data.transactions?.length === 0 && (
                        <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">
                          <div className="text-sm font-medium">No transactions found</div>
                          <div className="text-xs mt-1">Try a different period, branch, or account.</div>
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

